import {
  CHARACTER_KINDS,
  type CharacterDto,
  type CharacterKind,
  LANGUAGES,
  type MediaDto,
  STYLE_PRESETS,
} from '@studio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Sparkles, Square, Trash2, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ImageSlots } from '@/components/ImageSlots';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, NativeSelect } from '@/components/ui/controls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/form';
import { EmptyState, ErrorAlert, PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { isActive, useBackgrounds, useCharacters } from '@/lib/queries';

const KIND_LABEL: Record<CharacterKind, string> = {
  organ: 'Cartoon organ',
  person: 'Person',
  mascot: 'Mascot',
  product: 'Product character',
};

interface Draft {
  name: string;
  kind: CharacterKind;
  callName: string;
  visualDescription: string;
  voiceDescription: string;
  language: string;
  images: MediaDto[];
  notes: string;
  /** The clothes picture (0 or 1). */
  outfit: MediaDto[];
  backgroundId: string | null;
}

const empty: Draft = {
  name: '',
  kind: 'person',
  callName: '',
  visualDescription: '',
  voiceDescription: '',
  language: 'Hindi',
  images: [],
  notes: '',
  outfit: [],
  backgroundId: null,
};

function CharacterDialog({
  character,
  open,
  onOpenChange,
}: {
  character: CharacterDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const { data: backgrounds = [] } = useBackgrounds();
  const [d, setD] = useState<Draft>(empty);
  useEffect(() => {
    if (open)
      setD(
        character
          ? { ...character, outfit: character.outfitImage ? [character.outfitImage] : [], backgroundId: character.backgroundId ?? null }
          : empty,
      );
  }, [open, character]);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: d.name,
        kind: d.kind,
        callName: d.callName,
        visualDescription: d.visualDescription,
        voiceDescription: d.voiceDescription,
        language: d.language,
        notes: d.notes,
        imageIds: d.images.map((i) => i.id),
        outfitImageId: d.outfit[0]?.id ?? null,
        backgroundId: d.backgroundId,
      };
      return character
        ? api(`/api/characters/${character.id}`, { method: 'PATCH', body })
        : api('/api/characters', { method: 'POST', body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['characters'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      toast.success('Character saved');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{character ? `Edit ${character.name}` : 'New character'}</DialogTitle>
          <DialogDescription>
            Descriptions are in English: they go into the prompts. The same words every time keep the character looking
            the same.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            {(p) => (
              <Input {...p} value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Stomach" />
            )}
          </Field>
          <Field label="Kind">
            {(p) => (
              <NativeSelect {...p} value={d.kind} onChange={(e) => set('kind', e.target.value as CharacterKind)}>
                {CHARACTER_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Called in prompts" hint='How a sentence names it, e.g. "the stomach". Blank: the name.'>
            {(p) => <Input {...p} value={d.callName} onChange={(e) => set('callName', e.target.value)} />}
          </Field>
          <Field label="Default language">
            {(p) => (
              <NativeSelect {...p} value={d.language} onChange={(e) => set('language', e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field
            label="What it looks like"
            className="sm:col-span-2"
            hint="e.g. a chubby, glossy salmon-pink cartoon stomach with big round eyes and thin pink arms and legs"
          >
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={d.visualDescription}
                onChange={(e) => set('visualDescription', e.target.value)}
              />
            )}
          </Field>
          <Field label="Voice" className="sm:col-span-2" hint="e.g. a bright, cheerful young male voice">
            {(p) => (
              <Input {...p} value={d.voiceDescription} onChange={(e) => set('voiceDescription', e.target.value)} />
            )}
          </Field>
          <div className="sm:col-span-2">
            <div className="mb-1.5 text-sm font-medium">Reference pictures (1–3)</div>
            <ImageSlots images={d.images} max={3} onChange={(images) => set('images', images)} />
            <p className="mt-1 text-xs text-muted-foreground">
              Used when a shot shows this character with "reference pictures" — the video keeps its look.
            </p>
          </div>
          <div>
            <div className="mb-1.5 text-sm font-medium">Outfit (what they wear)</div>
            <ImageSlots images={d.outfit} max={1} onChange={(outfit) => set('outfit', outfit)} />
            <p className="mt-1 text-xs text-muted-foreground">
              A picture of the clothes: a dress, a uniform, a product photo. The character sheet is drawn wearing exactly
              these clothes (press <i>Redraw all</i> on the project's Characters tab after changing it).
            </p>
          </div>
          <Field
            label="Background (locked)"
            hint="Every still and shot with this character is set in this place. Add places on the Backgrounds page."
          >
            {(p) => (
              <NativeSelect {...p} value={d.backgroundId ?? ''} onChange={(e) => set('backgroundId', e.target.value || null)}>
                <option value="">— none —</option>
                {backgrounds.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            {(p) => <Textarea {...p} rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />}
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!d.name.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A full-body picture on a plain backdrop, drawn on the GPU from the description. It becomes picture 1: storyboard
 * stills place the character into the scene from it, so the same face and clothes show up in every shot.
 */
function CastPicture({ c }: { c: CharacterDto }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [style, setStyle] = useState<string>(c.kind === 'person' ? STYLE_PRESETS[1] : STYLE_PRESETS[0]);
  const job = c.castJob;
  const busy = !!job && isActive(job.status);
  const done = (r: CharacterDto) => {
    qc.setQueryData<CharacterDto[]>(['characters'], (list) => list?.map((x) => (x.id === r.id ? r : x)));
    void qc.invalidateQueries({ queryKey: ['gpu'] });
  };
  const make = useMutation({
    mutationFn: () => api<CharacterDto>(`/api/characters/${c.id}/cast-picture`, { method: 'POST', body: { style } }),
    onSuccess: (r) => {
      done(r);
      toast.success('Drawing the cast picture on the GPU');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: () => api<CharacterDto>(`/api/characters/${c.id}/cast-picture/cancel`, { method: 'POST' }),
    onSuccess: done,
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (busy)
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" /> {job.progressNote ?? 'Drawing the cast picture…'}
        <Button variant="ghost" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={() => cancel.mutate()}>
          <Square /> Cancel
        </Button>
      </div>
    );
  const full = c.images.length >= 3;
  if (me && !me.imagesAvailable) return null;
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <NativeSelect className="h-7 flex-1 text-xs" value={style} onChange={(e) => setStyle(e.target.value)}>
          {STYLE_PRESETS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </NativeSelect>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          onClick={() => make.mutate()}
          loading={make.isPending}
          disabled={full || !c.visualDescription.trim()}
          title={
            full
              ? 'This character has 3 pictures — remove one first'
              : !c.visualDescription.trim()
                ? 'Write what it looks like first'
                : 'Draw front, three-quarter and face pictures on the GPU'
          }
        >
          <Sparkles /> Make character sheet
        </Button>
      </div>
      {job?.status === 'failed' && <p className="text-xs text-destructive">Cast picture failed: {job.error}</p>}
    </div>
  );
}

export function CharactersPage() {
  const qc = useQueryClient();
  const q = useCharacters();
  const [editing, setEditing] = useState<CharacterDto | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<CharacterDto | null>(null);
  const del = useMutation({
    mutationFn: (c: CharacterDto) => api(`/api/characters/${c.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['characters'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Characters"
        description="People, cartoon organs, mascots — reusable in every project."
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus /> New character
          </Button>
        }
      />
      {q.error && <ErrorAlert message={errorMessage(q.error)} />}
      {q.isLoading && <Skeleton className="h-48" />}
      {q.data && !q.data.length && <EmptyState icon={<UserRound />} title="No characters yet" />}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {q.data?.map((c) => (
          <Card key={c.id} className="space-y-2 p-3">
            <div className="flex gap-3">
            <button
              type="button"
              className="flex min-w-0 flex-1 gap-3 text-left"
              onClick={() => {
                setEditing(c);
                setOpen(true);
              }}
            >
              <div className="grid h-28 w-20 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                {c.images[0] ? (
                  <img src={c.images[0].url} alt="" className="size-full object-cover" />
                ) : (
                  <UserRound className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 space-y-1">
                <div className="font-medium">{c.name}</div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="secondary">{KIND_LABEL[c.kind]}</Badge>
                  <Badge variant="outline">
                    {c.images.length} picture{c.images.length === 1 ? '' : 's'}
                  </Badge>
                </div>
                <p className="line-clamp-3 text-xs text-muted-foreground">
                  {c.visualDescription || 'No description yet'}
                </p>
                {c.voiceDescription && <p className="line-clamp-1 text-xs">🔊 {c.voiceDescription}</p>}
                <div className="flex flex-wrap gap-1">
                  {c.outfitImage && <Badge variant="outline">👗 outfit set</Badge>}
                  {c.background && <Badge variant="secondary">🔒 {c.background.name}</Badge>}
                </div>
              </div>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground"
              onClick={() => setDeleting(c)}
              aria-label={`Delete ${c.name}`}
            >
              <Trash2 />
            </Button>
            </div>
            <CastPicture c={c} />
          </Card>
        ))}
      </div>
      <CharacterDialog character={editing} open={open} onOpenChange={setOpen} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete ${deleting?.name}?`}
        description="Projects stop listing it; shots that mention it keep their text."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </div>
  );
}
